import { Button } from "$components/button/button";
import { ChevronDownIcon } from "$components/icons";
import {
  type MenuItem,
  PopoverMenu,
} from "$components/popover-menu/popover-menu";
import { PALETTE } from "$global-providers/mui-provider/palette";
import {
  NetworkStatus,
  useNetworkStatus,
} from "$global-providers/network-status-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { usePatientQueueDropdownLazyQuery } from "$routes/patient-queue/_lib/__generated__/graphql.gql";
import {
  useAddPatientToQueue,
  useRemovePatientFromQueue,
} from "$routes/patient-queue/_lib/graphql";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { PatientCentrePatient } from ".";

export const QueueMenu: React.FC<{ patient: PatientCentrePatient }> = ({
  patient,
}) => {
  const CURRENT_QUEUE_TEXT = "Current queue";

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { formatMessage } = useIntl();
  const [fetchQueues, { data, loading, error }] =
    usePatientQueueDropdownLazyQuery({
      variables: { patientUuid: patient.uuid },
      onError: () =>
        toast.error(
          formatMessage({
            id: "patientQueueing.dropdown.fetch.error",
            defaultMessage: "Failed to fetch queues for facility",
          }),
        ),
    });
  const { networkStatus } = useNetworkStatus();
  const queueForPatientId = data?.queueForPatient?._id;

  const [addPatientToQueue] = useAddPatientToQueue(
    patient.uuid,
    queueForPatientId,
  );
  const [removePatientFromQueue] = useRemovePatientFromQueue(patient.uuid);

  useEffect(() => {
    if (!data && networkStatus === NetworkStatus.ONLINE && !loading && !error) {
      fetchQueues();
    }
  }, [networkStatus, loading, fetchQueues, data, error]);

  const queueMenuItems: MenuItem[] = useMemo(() => {
    const queues = data?.facilityQueues ?? [];

    if (!queues.length) {
      return [];
    }

    const queueOptions = queues
      .map((queue) => ({
        defaultMessage: queue.name,
        id: queue._id,
        icon: null,
        onClick: () => {
          addPatientToQueue(queue._id);
          setIsMenuOpen(false);
        },
        subText: queue._id === queueForPatientId ? CURRENT_QUEUE_TEXT : null,
      }))
      .sort((a, b) => a.defaultMessage.localeCompare(b.defaultMessage));

    return queueOptions;
  }, [queueForPatientId, data, addPatientToQueue]);

  return (
    <>
      <Button
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          setIsMenuOpen(true);
        }}
        variant="outlined"
        color="primary"
        endIcon={
          <ChevronDownIcon
            style={{
              width: 28,
              height: 28,
              transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        }
      >
        {queueForPatientId ? (
          <FormattedMessage
            id="patient-centre.change-queue"
            defaultMessage="Change queue"
          />
        ) : (
          <FormattedMessage
            id="patient-centre.add-to-queue"
            defaultMessage="Add to queue"
          />
        )}
      </Button>

      {isMenuOpen && (
        <PopoverMenu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          primaryMenuItems={queueMenuItems}
          secondaryMenuItems={
            queueForPatientId
              ? [
                  {
                    defaultMessage: "Remove from queue",
                    id: "remove_from_queue",
                    textStyle: {
                      color: PALETTE.error.main,
                      fontWeight: "bold",
                    },
                    icon: null,
                    onClick: () => {
                      removePatientFromQueue();
                      setIsMenuOpen(false);
                    },
                  },
                ]
              : []
          }
        />
      )}
    </>
  );
};
